import { Text, View } from 'react-native'
import { useTranslation } from 'react-i18next'
import { Check } from '@/components/ui/icons'
import { Pressable } from '@/components/ui/Pressable'
import { useTheme } from '@/lib/hooks/use-theme'
import { INTERACTION, useSemanticColors } from '@/lib/theme/tokens'
import { FONTS, RADIUS, SPACING } from '@/lib/brand'
import type { TourExperienceStop } from '@/lib/types/tours.types'
import { orderTourStops } from '@/lib/maps/tour-stop-layout'
import { TourStopMarker } from './TourStopMarker'

interface TourTripTimelineProps {
  stops: TourExperienceStop[]
  currentPosition: number
  selectedStopId?: string | null
  onStopPress?: (stopId: string) => void
}

export function TourTripTimeline({
  stops,
  currentPosition,
  selectedStopId,
  onStopPress,
}: TourTripTimelineProps) {
  const { t } = useTranslation()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const ordered = orderTourStops(stops)

  return (
    <View>
      {ordered.map((stop, index) => {
        const number = index + 1
        const completed = stop.position < currentPosition
        const current = stop.position === currentPosition
        const selected = stop.id === selectedStopId
        const last = index === ordered.length - 1
        return (
          <Pressable
            key={stop.id}
            onPress={() => onStopPress?.(stop.id)}
            disabled={!onStopPress}
            accessibilityRole="button"
            accessibilityState={{ selected }}
            accessibilityLabel={t('tours.stop_marker', {
              number,
              label: stop.label,
            })}
            testID={`tours.stop-row-${number}`}
            style={{
              minHeight: last ? INTERACTION.minTouchTarget : 66,
              flexDirection: 'row',
              borderRadius: RADIUS.lg,
              backgroundColor: selected || current ? colors.surfaceSubtle : 'transparent',
              paddingHorizontal: SPACING.xs,
            }}
          >
            <View style={{ width: 34, alignItems: 'center' }}>
              <TourStopMarker
                number={number}
                kind={stop.kind}
                total={ordered.length}
                selected={selected || current}
                accessibilityLabel={t('tours.stop_marker', {
                  number,
                  label: stop.label,
                })}
              />
              {!last ? (
                <View
                  style={{
                    width: 2,
                    flex: 1,
                    backgroundColor: completed || current ? colors.success : colors.border,
                  }}
                />
              ) : null}
            </View>
            <View
              style={{
                flex: 1,
                paddingLeft: SPACING.sm,
                paddingBottom: SPACING.md,
                justifyContent: 'center',
              }}
            >
              <Text
                style={{
                  fontFamily: FONTS.body,
                  fontSize: 16,
                  lineHeight: 22,
                  color: current || selected ? colors.textPrimary : colors.textSecondary,
                }}
              >
                {stop.label}
              </Text>
            </View>
            {completed ? (
              <View style={{ justifyContent: 'center', paddingRight: SPACING.xs }}>
                <Check size={16} color={colors.success} strokeWidth={2.4} />
              </View>
            ) : null}
          </Pressable>
        )
      })}
    </View>
  )
}
